import { SubscriptionPlansModal } from "@/components/subscription-plans-modal";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { usePricingLimits } from "@/hooks/use-pricing-limits";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { AlertTriangle, Home, Zap } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";

interface PropertyLimitIndicatorProps {
  currentCount: number;
}

export function PropertyLimitIndicator({
  currentCount,
}: PropertyLimitIndicatorProps) {
  const router = useRouter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    propertyLimit: limit,
    isOverPropertyLimit: isOverLimit,
    remainingProperties,
    shouldShowPropertyWarning,
    tier,
    isPlus,
  } = usePricingLimits({ propertyCount: currentCount });

  if (!shouldShowPropertyWarning) return null;

  const percentage = Math.min((currentCount / limit) * 100, 100);

  function handleUpgradeClick() {
    if (isPlus) {
      router.push("/app/settings?tab=subscription");
    } else {
      setIsModalOpen(true);
    }
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          "rounded-lg p-4",
          isOverLimit ? "bg-red-50 border-red-100 border" : "bg-white border",
        )}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              {isOverLimit ? (
                <AlertTriangle className="h-5 w-5 text-red-600" />
              ) : (
                <Home className="h-5 w-5 text-primary" />
              )}
              <h3
                className={cn(
                  "font-semibold",
                  isOverLimit ? "text-red-600" : "text-smokeyBlack",
                )}
              >
                {isOverLimit ? "Property Limit Reached" : "Saved Properties"}
              </h3>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span
                className={cn(
                  "font-medium",
                  isOverLimit ? "text-red-600" : "text-darkGray",
                )}
              >
                {currentCount}/{limit}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <Progress
              value={percentage}
              className={cn(
                "h-2",
                isOverLimit ? "bg-red-100 [&>div]:bg-red-600" : "",
              )}
            />

            <div className="flex items-center justify-between gap-4">
              <p
                className={cn(
                  "text-sm",
                  isOverLimit ? "text-red-600" : "text-darkGray",
                )}
              >
                {isOverLimit
                  ? tier === "plus"
                    ? "You've reached the Plus plan limit. Upgrade to Pro for unlimited properties."
                    : "You've reached the free plan limit. Upgrade for more properties."
                  : `${remainingProperties} propert${remainingProperties === 1 ? "y" : "ies"} remaining on ${tier} plan`}
              </p>
              <Button
                onClick={handleUpgradeClick}
                className={cn(
                  "shrink-0 flex items-center gap-2",
                  isOverLimit
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-canary hover:bg-canary/90 text-smokeyBlack",
                )}
              >
                <Zap className="h-4 w-4" />
                {isPlus ? "Upgrade to Pro" : "Upgrade"}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>

      {!isPlus && (
        <SubscriptionPlansModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
}
